import { z } from 'zod';

/**
 * What the scoring model returns, as a structured output. Kept to the JSON
 * Schema subset structured outputs accept (no numeric bounds): ranges are
 * enforced in code by `normaliseAssessment`, not trusted from the model.
 */
export const EvidenceSchema = z.object({
  /** Verbatim (or translated-verbatim) words from the transcript. */
  quote: z.string(),
  /** Turn number the quote came from, as labelled in the transcript. */
  turn: z.number().int(),
});

export const CompetencyAssessmentSchema = z.object({
  key: z.string(),
  /** 1 (poor) - 5 (excellent); null when the interview never probed it. */
  score: z.number().int().nullable(),
  confidence: z.enum(['low', 'medium', 'high']),
  evidence: z.array(EvidenceSchema),
  rationale: z.string(),
});

export const FactAssessmentSchema = z.object({
  key: z.string(),
  /**
   * Normalised value as a string: a plain number in the fact's unit ("45"),
   * "true" / "false", or free text. null when the candidate never said.
   */
  value: z.string().nullable(),
  turn: z.number().int().nullable(),
  note: z.string().nullable(),
});

export const RecommendationSchema = z.enum(['STRONG_YES', 'YES', 'MAYBE', 'NO']);
export type Recommendation = z.infer<typeof RecommendationSchema>;

export const AssessmentSchema = z.object({
  competencies: z.array(CompetencyAssessmentSchema),
  facts: z.array(FactAssessmentSchema),
  englishLevel: z.enum(['not_assessed', 'basic', 'conversational', 'professional', 'fluent']),
  primaryLanguage: z.string(),
  summary: z.string(),
  strengths: z.array(z.string()),
  concerns: z.array(z.string()),
  /** Things a recruiter must look at: contradictions, possible reading from a script, misconduct. */
  redFlags: z.array(z.string()),
  /** Questions the candidate asked that the interviewer could not answer. */
  openQuestions: z.array(z.string()),
  completeness: z.enum(['complete', 'partial', 'insufficient']),
  recommendation: RecommendationSchema,
});
export type Assessment = z.infer<typeof AssessmentSchema>;
